import React, { useState } from 'react';
import { toast } from 'react-toastify';
import supplierApi from '../../services/supplierApi';

/**
 * SupplierLogoUpload Component
 * 
 * Reusable component for uploading/updating supplier logo
 * Used in supplier dashboard and profile management
 */
function SupplierLogoUpload({ currentLogo, supplierCode, supplierName, onLogoUpdated, onClose }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentLogo || null);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a valid image file (JPG, PNG, GIF)');
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('Logo file size must be less than 5MB');
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = (event) => {
      setPreview(event.target.result);
    };
    reader.readAsDataURL(file);

    // Upload to server
    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('supplier_id', supplierCode);
      formData.append('uploaded_by_name', supplierName || 'Supplier');
      formData.append('document_type', 'logo');
      formData.append('is_public', '1');

      const response = await supplierApi.post('/api/attachments/suppliers', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.status === 1) {
        toast.success('Logo uploaded successfully!');
        
        // Notify parent component to refresh data
        if (onLogoUpdated) {
          onLogoUpdated(response.data.data);
        }
        
        // Close modal after short delay
        setTimeout(() => {
          if (onClose) onClose();
        }, 1000);
      } else {
        toast.error('Failed to upload logo');
        setPreview(currentLogo); // Revert to original
      }
    } catch (error) {
      console.error('Logo upload error:', error);
      toast.error(error.response?.data?.message || 'Failed to upload logo');
      setPreview(currentLogo); // Revert to original
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = () => {
    setPreview(null);
    // Note: To actually delete from server, you'd need a delete endpoint
    // For now, just clear the preview
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Upload Company Logo
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={uploading}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4">
          {/* Current Logo Preview */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Logo
            </label>
            <div className="flex items-center justify-center h-40 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg">
              {preview ? (
                <div className="relative">
                  <img
                    src={preview}
                    alt="Logo preview"
                    className="max-h-36 max-w-full object-contain"
                    onError={(e) => {
                      console.error('Logo preview failed to load');
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              ) : (
                <div className="text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="mt-2 text-sm text-gray-500">No logo uploaded</p>
                </div>
              )}
            </div>
          </div>

          {/* File Upload */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {preview ? 'Replace Logo' : 'Upload New Logo'}
            </label>
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif"
              onChange={handleFileSelect}
              disabled={uploading}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <p className="mt-2 text-xs text-gray-500">
              Accepted formats: JPG, PNG, GIF. Max size: 5MB
            </p>
          </div>

          {/* Guidelines */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <h4 className="text-sm font-semibold text-blue-900 mb-2">Logo Guidelines</h4>
            <ul className="text-xs text-blue-800 space-y-1">
              <li>• Use a high-quality image for best results</li>
              <li>• Square or landscape orientation works best</li>
              <li>• Transparent backgrounds are recommended</li>
              <li>• Your logo will appear on quotations and documents</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            disabled={uploading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          {uploading && (
            <div className="flex items-center text-sm text-gray-600">
              <svg className="animate-spin h-5 w-5 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Uploading...
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SupplierLogoUpload;
